import { useQuery } from '@tanstack/react-query'
import { Panel } from '../../../components/Panel'
import { HeatGrid } from '../../../components/charts/HeatGrid'
import { DeskFrame } from './DeskFrame'
import { NotYetLive } from './NotYetLive'
import { GroupNote } from './GroupNote'
import { useDeskState } from './useDeskState'
import { Failed, Loading } from './ArticleBits'
import { deskApi, type AudienceResp, type DeskPub, type Rate } from '../../../api/visitorsDesk'
import { heatCells, locationRows, weekBars } from '../../../lib/deskAudience'
import { td, th, mono } from '../../../lib/deskArticles'
import { rateWithCount } from '../../../lib/deskFormat'
import { fmtDate, fmtInt } from '../../../lib/format'

const FREQ_NOTE = 'This is the reader ladder’s raw material: the long tail of 1-day visitors and the small core who come back.'
const LOCATION_NOTE = 'US states and Canadian provinces from the visitor’s network location.'
const DEVICE_NOTE = 'Last column: share of visits that were real reads, with the visit count.'
const WHO_NOTE = 'People, then pages per person.'
const HEAT_NOTE = 'Darker = more people. Use it to choose when to send the newsletter and publish.'
const TONE = { cyan: '#5eeaff', amber: '#ffb648' } as const

const share = (s: number | null) => (s === null ? '—' : `${Math.round(s * 100)}%`)
const week = (iso: string) => fmtDate(`${iso}T12:00:00Z`, { month: 'short', day: 'numeric', timeZone: 'UTC' })

/** `visitors/desk/:pub/audience`. */
export function DeskAudience() {
  return <DeskFrame tab="audience"><AudienceBody /></DeskFrame>
}

export function AudienceBody() {
  const { pub, q } = useDeskState('audience')
  const query = useQuery({
    queryKey: ['desk', 'audience', pub, q.period, q.from, q.to, q.compare, q.group],
    queryFn: () => deskApi.audience(pub as DeskPub, q), retry: false, placeholderData: (prev) => prev,
  })
  const d = query.data
  if (query.isError) return <Failed what="the audience" />
  if (!d) return <Loading />
  if (d.unavailable !== undefined) return <NotYetLive text={d.unavailable} />
  return (
    <div className="space-y-4">
      <GroupNote applied={d.group_applied} />
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <NewReturning d={d} />
        <Frequency d={d} />
      </div>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Where d={d} />
        <Devices d={d} />
        <Who d={d} />
      </div>
      <Heat d={d} />
    </div>
  )
}

type Ok = AudienceResp & { unavailable?: undefined }

function backSentence(nvr: AudienceResp['new_vs_returning']): string {
  const a: Rate = nvr.back_within_7d
  const b: Rate = nvr.back_within_28d
  if (a.rate === null && b.rate === null) return 'Collecting: not enough people yet to say how many come back.'
  if (a.rate === null) return `Of people new in a week, ${rateWithCount(b.rate, b.numerator)} came back within 4 weeks (within 7 days: collecting).`
  if (b.rate === null) return `Of people new in a week, ${rateWithCount(a.rate, a.numerator)} came back within 7 days (within 4 weeks: collecting).`
  return `Of people new in a week, ${rateWithCount(a.rate, a.numerator)} came back within 7 days and ${rateWithCount(b.rate, b.numerator)} within 4 weeks.`
}

function NewReturning({ d }: { d: Ok }) {
  const bars = weekBars(d.new_vs_returning.weeks)
  const top = Math.max(1, ...bars.map((b) => b.total))
  const summary = bars.map((b) => `${week(b.weekStart)}: ${b.parts[1].value} new, ${b.parts[0].value} returning${b.incomplete ? ' (week not finished)' : ''}`).join('; ')
  return (
    <Panel label="New vs returning people, by week" accent="cyan">
      <div className="mb-2 flex gap-4 text-[12px] text-paper-dim">
        <span><span aria-hidden="true" style={{ color: TONE.cyan }}>■</span> Returning</span>
        <span><span aria-hidden="true" style={{ color: TONE.amber }}>■</span> New</span>
        <span className="text-paper-faint">Dotted = week not finished</span>
      </div>
      <div className="overflow-x-auto">
      <div role="img" aria-label={`New and returning people by week. ${summary}`} className="flex h-[160px] min-w-max items-end gap-2 border-b border-ink-500">
        {bars.map((b) => (
          <div key={b.weekStart} title={`${week(b.weekStart)}: ${b.parts[1].value} new, ${b.parts[0].value} returning${b.incomplete ? ' (so far)' : ''}`}
               className="flex min-w-[16px] flex-1 flex-col-reverse" style={{ height: `${(b.total / top) * 100}%`, outline: b.incomplete ? '1px dotted #837e73' : undefined, opacity: b.incomplete ? 0.75 : 1 }}>
            {b.parts.map((p) => <span key={p.label} style={{ flexGrow: p.value, background: TONE[p.tone] }} />)}
          </div>
        ))}
      </div>
      </div>
      <div className="flex justify-between font-mono text-[11px] text-paper-faint">
        <span>{bars.length ? week(bars[0].weekStart) : ''}</span><span>{bars.length ? week(bars[bars.length - 1].weekStart) : ''}</span>
      </div>
      <details className="mt-2 font-mono text-[11px] text-paper-dim">
        <summary className="cursor-pointer text-paper-faint">Show the numbers</summary>
        <ul className="mt-1">{bars.map((b) => <li key={b.weekStart}>Week of {week(b.weekStart)}: {fmtInt(b.parts[1].value)} new, {fmtInt(b.parts[0].value)} returning{b.incomplete ? ' (so far)' : ''}</li>)}</ul>
      </details>
      <p className="mt-2 text-[12px] text-paper-faint">{backSentence(d.new_vs_returning)}</p>
    </Panel>
  )
}

function Frequency({ d }: { d: Ok }) {
  const top = Math.max(1, ...d.frequency.map((f) => f.people))
  return (
    <Panel label="How often people visit · days in the last 28" accent="amber">
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <tbody>
            {d.frequency.map((f) => (
              <tr key={f.label} className="border-t border-ink-600">
                <td className={`${td} w-[110px] whitespace-nowrap`}>{f.label}</td>
                <td className={td}><div className="h-2 min-w-[60px] bg-ink-600" aria-hidden="true"><div className="h-2 bg-amber-glow" style={{ width: `${(f.people / top) * 100}%` }} /></div></td>
                <td className={`${td} w-[70px] text-right font-mono`} style={mono}>{fmtInt(f.people)}</td>
                <td className={`${td} w-[56px] text-right font-mono text-paper-faint`} style={mono}>{share(f.share)}</td>
              </tr>
            ))}
            {d.frequency.length === 0 && <tr><td className={`${td} text-paper-faint`}>No visits yet</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-[12px] text-paper-faint">{FREQ_NOTE}</p>
    </Panel>
  )
}

function Where({ d }: { d: Ok }) {
  const rows = locationRows(d.locations)
  return (
    <Panel label="Where they are" accent="violet">
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead><tr><th className={th}>Place</th><th className={`${th} text-right`}>People</th><th className={`${th} text-right`}>Share</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-t border-ink-600">
                <td className={`${td} ${r.nested ? 'pl-4 text-paper-dim' : ''}`}><span className="block max-w-[200px] truncate" title={r.label}>{r.label}</span></td>
                <td className={`${td} text-right font-mono`} style={mono}>{fmtInt(r.people)}</td>
                <td className={`${td} text-right font-mono text-paper-faint`} style={mono}>{share(r.share)}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={3} className={`${td} text-paper-faint`}>No locations yet</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-[12px] text-paper-faint">{LOCATION_NOTE}</p>
    </Panel>
  )
}

function Devices({ d }: { d: Ok }) {
  const top = Math.max(1, ...d.devices.map((x) => x.people))
  return (
    <Panel label="Device" accent="green">
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead><tr><th className={th}>Device</th><th className={th} /><th className={`${th} text-right`}>People</th><th className={`${th} text-right`}>Real reads</th></tr></thead>
          <tbody>
            {d.devices.map((x) => (
              <tr key={x.device} className="border-t border-ink-600">
                <td className={td}>{x.device}</td>
                <td className={td}><div className="h-2 min-w-[40px] bg-ink-600" aria-hidden="true"><div className="h-2 bg-radar-green" style={{ width: `${(x.people / top) * 100}%` }} /></div></td>
                <td className={`${td} text-right font-mono`} style={mono}>{fmtInt(x.people)} <span className="text-paper-faint">{share(x.share)}</span></td>
                <td className={`${td} text-right font-mono text-paper-faint`} style={mono}>{rateWithCount(x.real_read_rate, x.visits)}</td>
              </tr>
            ))}
            {d.devices.length === 0 && <tr><td colSpan={4} className={`${td} text-paper-faint`}>No visits yet</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-[12px] text-paper-faint">{DEVICE_NOTE}</p>
    </Panel>
  )
}

function Who({ d }: { d: Ok }) {
  return (
    <Panel label="Who they are to us" accent="amber">
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead><tr><th className={th}>Group</th><th className={`${th} text-right`}>People</th><th className={`${th} text-right`}>Pages each</th></tr></thead>
          <tbody>
            {d.who.map((w) => (
              <tr key={w.access} className="border-t border-ink-600">
                <td className={td}>
                  {w.label}
                  {w.access === 'lapsed' && w.recent !== undefined && w.older !== undefined && (
                    <span className="block text-[11px] text-paper-faint">{fmtInt(w.recent)} within 90 days · {fmtInt(w.older)} older</span>
                  )}
                </td>
                <td className={`${td} text-right font-mono`} style={mono}>{fmtInt(w.people)}</td>
                <td className={`${td} text-right font-mono text-paper-faint`} style={mono}>{w.pages_per_person === null ? '—' : w.pages_per_person.toFixed(1)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-[12px] text-paper-faint">{WHO_NOTE}</p>
    </Panel>
  )
}

function Heat({ d }: { d: Ok }) {
  const cells = heatCells(d.heat.cells)
  return (
    <Panel label="When they read · Eastern time" accent="cyan">
      <HeatGrid ariaLabel="People by weekday and three-hour block, Eastern time" columns={d.heat.blocks}
                rows={d.heat.days.map((day, i) => ({ label: day, cells: cells[i] ?? [] }))} />
      <p className="mt-2 text-[12px] text-paper-faint">{HEAT_NOTE}</p>
    </Panel>
  )
}
